/**
 * Format a Date object as an ISO date string (YYYY-MM-DD).
 */
export function formatISODate(date) {
    const year = date.getFullYear();
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const day = date.getDate().toString().padStart(2, '0');
    return `${year}-${month}-${day}`;
}
/**
 * Parse an ISO date string (YYYY-MM-DD) into a Date at midnight local time.
 */
export function parseISODate(iso) {
    const [year, month, day] = iso.split('-').map(Number);
    return new Date(year, month - 1, day);
}
/**
 * Get the JavaScript day-of-week (0=Sunday through 6=Saturday) for an ISO date string.
 */
export function getDayOfWeek(iso) {
    return parseISODate(iso).getDay();
}
/**
 * Check whether a date string falls within [start, end] inclusive.
 */
export function isDateInRange(date, start, end) {
    return date >= start && date <= end;
}
/**
 * Add (or subtract) days from an ISO date string, returning a new ISO date string.
 */
export function addDays(date, days) {
    const d = parseISODate(date);
    d.setDate(d.getDate() + days);
    return formatISODate(d);
}
/**
 * Return the number of days between two ISO date strings.
 * Positive if end > start, negative if end < start, zero if equal.
 */
export function daysBetween(start, end) {
    const startDate = parseISODate(start);
    const endDate = parseISODate(end);
    const msPerDay = 86_400_000;
    return Math.round((endDate.getTime() - startDate.getTime()) / msPerDay);
}
/**
 * Generate all dates matching meeting patterns within [start, end].
 * Returns sorted array of ISO date strings.
 */
export function generateSessionDates(patterns, start, end) {
    if (patterns.length === 0 || start > end) {
        return [];
    }
    const daysOfWeek = new Set(patterns.map((p) => p.dayOfWeek));
    const dates = [];
    let current = start;
    while (current <= end) {
        if (daysOfWeek.has(getDayOfWeek(current))) {
            dates.push(current);
        }
        current = addDays(current, 1);
    }
    return dates;
}
/**
 * Get a calendar month grid as a 2D array of ISO date strings (weeks × 7 days).
 * Includes leading/trailing days from adjacent months so every row is a full week.
 * Weeks start on Sunday (day 0).
 */
export function getMonthCalendar(year, month) {
    // month is 0-indexed (JavaScript convention)
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0); // last day of month
    const grid = [];
    let currentWeek = [];
    // Pad the start of the first week with days from the previous month
    const firstDayOfWeek = firstDay.getDay(); // 0=Sunday
    for (let i = 0; i < firstDayOfWeek; i++) {
        const d = new Date(year, month, 1 - firstDayOfWeek + i);
        currentWeek.push(formatISODate(d));
    }
    // Fill in all days of the month
    const daysInMonth = lastDay.getDate();
    for (let day = 1; day <= daysInMonth; day++) {
        const d = new Date(year, month, day);
        currentWeek.push(formatISODate(d));
        if (currentWeek.length === 7) {
            grid.push(currentWeek);
            currentWeek = [];
        }
    }
    // Pad the last week with days from the next month
    if (currentWeek.length > 0) {
        const remaining = 7 - currentWeek.length;
        for (let i = 1; i <= remaining; i++) {
            const d = new Date(year, month + 1, i);
            currentWeek.push(formatISODate(d));
        }
        grid.push(currentWeek);
    }
    return grid;
}
/**
 * Get the current date as an ISO date string (YYYY-MM-DD).
 */
export function todayISO() {
    return formatISODate(new Date());
}
/**
 * Format a date for display — e.g., "Mon, Jan 15, 2026".
 */
export function formatDisplayDate(iso) {
    const date = parseISODate(iso);
    return date.toLocaleDateString('en-US', {
        weekday: 'short',
        year: 'numeric',
        month: 'short',
        day: 'numeric',
    });
}
/**
 * Get the ISO date string for the start of a given month.
 */
export function startOfMonth(year, month) {
    return formatISODate(new Date(year, month, 1));
}
/**
 * Get the ISO date string for the end of a given month.
 */
export function endOfMonth(year, month) {
    return formatISODate(new Date(year, month + 1, 0));
}
//# sourceMappingURL=dates.js.map